import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import type { AddressType } from '@sweetgreen/garnish';
import { useBreakpointHelpers } from '@sweetgreen/garnish';

import { TransparentModal } from '../../../../components/TransparentModal';
import type { ModalStackChildProps } from '../../../../navigation/AppNavigation.props';
import { LocationLayout, MapView } from '../../../LocationScreen/components';
import { LocationSearchTypePicker } from '../../../LocationScreen/components/SearchBar/subcomponents';
import { useNavigationActions } from '../../../LocationScreen/hooks';
import { useLocationScreenData } from '../../../LocationScreen/LocationScreen.data';
import { useCancelReorder, useReorderState } from '../../hooks';
import {
  useReorderLeftIcon,
  useReorderRightIcon,
} from '../ReorderConfirmLocation/ReorderConfirmLocation.components';

export const ReorderChangeLocation = ({
  navigation,
}: ModalStackChildProps<'ReorderChangeLocation'>) => {
  const { currentStep } = useReorderState();
  const cancelReorder = useCancelReorder();
  const { isMobile } = useBreakpointHelpers();
  const data = useLocationScreenData();

  // The location search type picker is part of the header.
  const { onBack, onClose } = useNavigationActions();
  const leftIcon = useReorderLeftIcon();
  const rightIcon = useReorderRightIcon();
  const { locationSearchType, setLocationSearchType, setSelectedLocation } =
    data;
  const locationSearchTypePicker = useReorderSearchTypePicker({
    locationSearchType,
    setLocationSearchType,
    setSelectedLocation,
  });

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => locationSearchTypePicker,
      headerLeft: onBack && isMobile ? () => leftIcon : undefined,
      headerRight: onClose && isMobile ? () => rightIcon : undefined,
      headerShown: isMobile,
    });
  }, [
    navigation,
    locationSearchTypePicker,
    leftIcon,
    rightIcon,
    isMobile,
    onBack,
    onClose,
  ]);

  if (currentStep !== 'locationSearch' && Platform.OS === 'web') return null;

  return (
    <TransparentModal
      testID="reorder-change-location"
      disableAnimation={isMobile}
      size="large"
      onClose={cancelReorder}
    >
      <LocationLayout
        locationSearchTypePicker={locationSearchTypePicker}
        {...data}
      >
        <MapView {...data} />
      </LocationLayout>
    </TransparentModal>
  );
};

// For mobile header.
const useReorderSearchTypePicker = ({
  locationSearchType,
  setLocationSearchType,
  setSelectedLocation,
}: {
  readonly locationSearchType: AddressType;
  readonly setLocationSearchType: (
    value: React.SetStateAction<AddressType>,
  ) => void;
  readonly setSelectedLocation: (value: React.SetStateAction<string>) => void;
}) => {
  const { onBack, onClose } = useNavigationActions();
  const { isMobile } = useBreakpointHelpers();
  const leftIcon = useReorderLeftIcon();
  const rightIcon = useReorderRightIcon();
  const handleSelect = React.useCallback(
    (searchType: AddressType) => {
      setLocationSearchType(searchType);
      setSelectedLocation('');
    },
    [setLocationSearchType, setSelectedLocation],
  );

  return (
    <View style={styles.chipsContainer}>
      {!isMobile && onBack ? leftIcon : null}

      <LocationSearchTypePicker
        searchType={locationSearchType}
        onSelect={handleSelect}
      />

      {!isMobile && onClose ? rightIcon : null}
    </View>
  );
};

const styles = StyleSheet.create({
  chipsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
});
